/**
 * slider.js — one carousel implementation for certificates, companies, lab
 * galleries and anything else that needs a horizontal rail.
 *
 * Why the old certificate slider did nothing when you clicked the arrows:
 *
 *   1. It called `track.scrollTo({ left: index * cardWidth })` with a positive
 *      offset. In an RTL container, `scrollLeft` is either negative (Firefox,
 *      Chrome ≥85) or inverted (legacy WebKit). A positive value is already
 *      past the start edge, so the browser clamped it and nothing moved.
 *   2. It measured `.nova-cert-card`, but the scroll unit was the wrapping
 *      `.nova-cert-slide`, so even in LTR the step size was wrong.
 *
 * The fix is to stop doing arithmetic on scrollLeft at all. `scrollIntoView`
 * on the target slide is direction-agnostic — the browser works out which
 * physical direction "the third slide" is in. Position is read back via
 * `getBoundingClientRect()` comparisons rather than raw scrollLeft, so the
 * sign convention never enters the picture.
 */

import {qs, qsa} from '../lib/dom.js';
import {gettext, num} from '../lib/i18n.js';

export class Slider {
    constructor(root, options = {}) {
        this.root = typeof root === 'string' ? qs(root) : root;
        if (!this.root) return;

        this.options = {
            autoplay: false,
            autoplayDelay: 6000,
            dots: true,
            loop: true,
            ...options,
        };

        this.track = qs('.nv-slider__track', this.root);
        this.prevBtn = qs('.nv-slider__nav--prev', this.root);
        this.nextBtn = qs('.nv-slider__nav--next', this.root);
        this.dotsEl = qs('.nv-slider__dots', this.root);

        if (!this.track) return;

        this.slides = qsa('.nv-slider__slide', this.track);
        if (!this.slides.length) return;

        this.index = 0;
        this.timer = null;
        this.destroyed = false;

        this._bind();
        this._buildDots();
        this._sync();

        if (this.options.autoplay) this.start();
    }

    /* -------------------------------------------------- binding */

    _bind() {
        this.prevBtn?.addEventListener('click', () => {
            this.stop();
            this.prev();
        });
        this.nextBtn?.addEventListener('click', () => {
            this.stop();
            this.next();
        });

        // Keep the active index honest when the user swipes or trackpad-scrolls.
        let scrollTimer = null;
        this.track.addEventListener('scroll', () => {
            clearTimeout(scrollTimer);
            scrollTimer = setTimeout(() => {
                this.index = this._nearestIndex();
                this._sync();
            }, 90);
        }, {passive: true});

        // Arrow keys operate in logical terms: "forward" advances regardless of
        // which physical arrow that is in the current direction.
        this.root.setAttribute('tabindex', '0');
        this.root.addEventListener('keydown', (event) => {
            const rtl = getComputedStyle(this.root).direction === 'rtl';
            if (event.key === 'ArrowLeft') {
                event.preventDefault();
                this.stop();
                rtl ? this.next() : this.prev();
            } else if (event.key === 'ArrowRight') {
                event.preventDefault();
                this.stop();
                rtl ? this.prev() : this.next();
            }
        });

        this.root.addEventListener('mouseenter', () => this.stop());
        this.root.addEventListener('focusin', () => this.stop());

        // Re-measure when the viewport changes the slides-per-view count.
        this._resizeObserver = new ResizeObserver(() => this._sync());
        this._resizeObserver.observe(this.track);
    }

    /* -------------------------------------------------- geometry */

    /** How many slides are fully visible at once. */
    _perView() {
        const trackWidth = this.track.clientWidth;
        const slideWidth = this.slides[0]?.getBoundingClientRect().width || 1;
        return Math.max(1, Math.round(trackWidth / slideWidth));
    }

    _maxIndex() {
        return Math.max(0, this.slides.length - this._perView());
    }

    /**
     * Index of the slide currently closest to the track's start edge.
     * Compared in viewport coordinates so RTL needs no special case: the
     * "start" edge is simply whichever edge the direction puts first.
     */
    _nearestIndex() {
        const trackBox = this.track.getBoundingClientRect();
        const rtl = getComputedStyle(this.root).direction === 'rtl';
        const trackStart = rtl ? trackBox.right : trackBox.left;

        let best = 0;
        let bestDistance = Infinity;

        this.slides.forEach((slide, i) => {
            const box = slide.getBoundingClientRect();
            const slideStart = rtl ? box.right : box.left;
            const distance = Math.abs(slideStart - trackStart);
            if (distance < bestDistance) {
                bestDistance = distance;
                best = i;
            }
        });

        return best;
    }

    /* -------------------------------------------------- movement */

    goTo(index) {
        if (this.destroyed) return;

        const max = this._maxIndex();
        let target = index;

        if (this.options.loop) {
            if (target > max) target = 0;
            if (target < 0) target = max;
        } else {
            target = Math.min(Math.max(target, 0), max);
        }

        this.index = target;

        const slide = this.slides[target];
        if (!slide) return;

        const rtl = getComputedStyle(this.root).direction === 'rtl';
        const trackBox = this.track.getBoundingClientRect();
        const slideBox = slide.getBoundingClientRect();
        const offset = rtl
            ? slideBox.right - trackBox.right
            : slideBox.left - trackBox.left;

        this.track.scrollBy({
            left: offset,
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
        });

        this._sync();
    }

    next() {
        this.goTo(this.index + 1);
    }

    prev() {
        this.goTo(this.index - 1);
    }

    /* -------------------------------------------------- state */

    _sync() {
        if (this.destroyed) return;

        const max = this._maxIndex();
        const fitsEntirely = max === 0;

        // Nothing to scroll: hide the chrome rather than show dead controls.
        this.root.classList.toggle('is-static', fitsEntirely);

        if (!this.options.loop) {
            if (this.prevBtn) this.prevBtn.disabled = this.index <= 0;
            if (this.nextBtn) this.nextBtn.disabled = this.index >= max;
        }

        if (this.dotsEl) {
            const dots = qsa('.nv-slider__dot', this.dotsEl);
            dots.forEach((dot, i) => {
                const active = i === this.index;
                dot.classList.toggle('is-active', active);
                dot.setAttribute('aria-current', active ? 'true' : 'false');
            });
        }
    }

    _buildDots() {
        if (!this.dotsEl || !this.options.dots) return;

        const max = this._maxIndex();
        const total = max + 1;

        this.dotsEl.innerHTML = Array.from({length: total}, (_, i) => `
      <button type="button" class="nv-slider__dot" data-index="${i}"
              aria-label="${gettext('Go to slide')} ${num(i + 1)}"></button>
    `).join('');

        this.dotsEl.addEventListener('click', (event) => {
            const dot = event.target.closest('.nv-slider__dot');
            if (!dot) return;
            this.stop();
            this.goTo(Number(dot.dataset.index));
        });
    }

    /* -------------------------------------------------- autoplay */

    start() {
        if (this.timer || this._maxIndex() === 0) return;
        this.timer = setInterval(() => this.next(), this.options.autoplayDelay);
    }

    stop() {
        clearInterval(this.timer);
        this.timer = null;
    }

    destroy() {
        this.stop();
        this._resizeObserver?.disconnect();
        this.destroyed = true;
    }
}

/** Registry so a re-render can tear down the previous page's sliders. */
const active = [];

export function initSliders(scope = document) {
    destroySliders();
    qsa('[data-slider]', scope).forEach((el) => {
        const instance = new Slider(el, {
            autoplay: el.dataset.sliderAutoplay === 'true',
            loop: el.dataset.sliderLoop !== 'false',
            dots: el.dataset.sliderDots !== 'false',
        });
        active.push(instance);
    });
}

export function destroySliders() {
    while (active.length) active.pop()?.destroy?.();
}

/**
 * Markup helper so every slider on the site shares one structure.
 * `slides` is an array of HTML strings, already escaped by the caller.
 */
export function sliderMarkup(slides, {
    id = '',
    variant = '',
    autoplay = false,
    dots = true,
    label = '',
} = {}) {
    if (!slides.length) return '';

    return `
    <div class="nv-slider ${variant}" ${id ? `id="${id}"` : ''} data-slider
         data-slider-autoplay="${autoplay}" data-slider-dots="${dots}"
         role="region" aria-roledescription="${gettext('carousel')}"
         ${label ? `aria-label="${label}"` : ''}>
      <button type="button" class="nv-slider__nav nv-slider__nav--prev"
              aria-label="${gettext('Previous')}">
        <i class="fa-solid fa-chevron-left nv-icon-flip" aria-hidden="true"></i>
      </button>
      <div class="nv-slider__viewport">
        <div class="nv-slider__track">
          ${slides.map((s) => `<div class="nv-slider__slide">${s}</div>`).join('')}
        </div>
      </div>
      <button type="button" class="nv-slider__nav nv-slider__nav--next"
              aria-label="${gettext('Next')}">
        <i class="fa-solid fa-chevron-right nv-icon-flip" aria-hidden="true"></i>
      </button>
      ${dots ? '<div class="nv-slider__dots"></div>' : ''}
    </div>`;
}
